import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Layout, Globe, ShoppingCart, Cpu, ArrowUpRight } from 'lucide-react';

const services = [
  {
    id: 'landing-pages',
    icon: Layout,
    title: 'Landing Pages',
    description: 'Páginas de conversão otimizadas para capturar leads e aumentar suas vendas. Design moderno e responsivo.',
    features: ['Alta Conversão', 'SEO Otimizado', 'Design Responsivo'],
  },
  {
    id: 'sites',
    icon: Globe,
    title: 'Sites Institucionais',
    description: 'Sites profissionais que representam sua marca com excelência. Presença digital impactante.',
    features: ['Multi-páginas', 'CMS Integrado', 'Performance'],
  },
  {
    id: 'ecommerce',
    icon: ShoppingCart,
    title: 'E-commerces',
    description: 'Lojas virtuais completas para vender seus produtos online. Integração com meios de pagamento.',
    features: ['Checkout Seguro', 'Gestão de Estoque', 'Integrações'],
  },
  {
    id: 'automacoes',
    icon: Cpu,
    title: 'Automações',
    description: 'Automatize processos repetitivos e aumente a eficiência do seu negócio com soluções inteligentes.',
    features: ['APIs & Webhooks', 'Fluxos Automatizados', 'Integrações'],
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      data-testid={`service-card-${service.id}`}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group service-card p-8 md:p-10 border border-brand-grey/20 bg-brand-dark/50 backdrop-blur-sm card-hover cursor-pointer"
    >
      {/* Icon */}
      <div className="mb-8 relative">
        <div className="relative overflow-hidden w-16 h-16 flex items-center justify-center border border-brand-light/30 text-brand-light transition-colors duration-500 before:content-[''] before:absolute before:-left-1 before:-top-1 before:w-[140%] before:h-[140%] before:bg-[radial-gradient(circle_at_0_0,_rgba(46,46,46,1)_0%,_rgba(46,46,46,0.92)_24%,_rgba(46,46,46,0.72)_46%,_rgba(46,46,46,0.35)_68%,_transparent_100%)] before:scale-0 before:origin-top-left before:transition-transform before:duration-700 before:ease-out group-hover:before:scale-[1.8] group-hover:text-brand-dark">
          <Icon size={28} strokeWidth={1.5} className="relative z-10" />
        </div>
      </div>

      {/* Title */}
      <h3
        data-testid={`service-title-${service.id}`}
        className="font-display text-2xl md:text-3xl font-bold text-brand-light mb-4 group-hover:text-brand-light transition-colors duration-300"
      >
        {service.title}
      </h3>

      {/* Description */}
      <p
        data-testid={`service-description-${service.id}`}
        className="font-body text-brand-light/60 leading-relaxed mb-6"
      >
        {service.description}
      </p>

      {/* Features */}
      <div className="flex flex-wrap gap-2 mb-8">
        {service.features.map((feature, i) => (
          <span
            key={i}
            className="px-3 py-1 text-xs uppercase tracking-wider font-medium text-brand-grey border border-brand-grey/30"
          >
            {feature}
          </span>
        ))}
      </div>

      {/* Arrow */}
      <div className="flex items-center gap-2 text-brand-light opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <span className="text-sm uppercase tracking-wider font-medium">Saiba mais</span>
        <ArrowUpRight size={16} />
      </div>
    </motion.div>
  );
};

export const Services = () => {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: "-100px" });

  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="py-24 md:py-32 relative"
    >
      {/* Background Accent */}
      <div className="absolute left-0 top-1/4 w-px h-1/2 bg-gradient-to-b from-transparent via-brand-beige/20 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div ref={headerRef} className="mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-12 h-px bg-brand-light" />
            <span
              data-testid="services-label"
              className="text-xs uppercase tracking-[0.2em] font-bold text-brand-light"
            >
              Nossos Serviços
            </span>
          </motion.div>

          <motion.h2
            data-testid="services-title"
            initial={{ opacity: 0, y: 30 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-brand-light"
          >
            Soluções completas
            <br />
            <span className="text-brand-light">para seu negócio</span>
          </motion.h2>
        </div>

        {/* Services Grid */}
        <div
          data-testid="services-grid"
          className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
        >
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
